<template>
  <el-cascader
    v-model="model"
    :placeholder="placeholder"
    :size="size"
    :options="selectOptions"
    :filterable="filterable"
    :clearable="clearable"
    :change-on-select="changeOnSelect"
    :props="props"
    :debounce="debounce"
    :expand-trigger="expandTrigger"
    :show-all-levels="showAllLevels"
    :popper-class="popperClass"
    :before-filter="beforeFilter"
    :separator="separator"
    :disabled="isDisabled"
    @active-item-change="onActiveItemChange"
    @blur="onBlur"
    @focus="onFocus"
    @visible-change="onVisibleChange"
    @change="onChange"
    v-loading="loading"
  ></el-cascader>
</template>

<script>
/* eslint-disable semi */
import { Component, Prop, Watch } from 'vue-property-decorator';
import SasComponent from './SasComponent';
import * as sas from '../decorators';

/**
 * カスケード要素の基底コンポーネント
 */
@Component
export default class SasCascader extends SasComponent {
  /*
   el-cascaderの下記属性については親コンポーネントからの指定不可
   全てコンポーネント側で管理します
   before-filter
   popper-class
   show-all-levels
   expand-trigger
   selectOptions
   props
   separator
   filterable
   change-on-select
   debounce
   必要に応じて、継承クラス側のコンポーネントで適宜指定してください。
   */

  /**
   * 親がv-modelでバインドしたデータの格納先プロパティ
   * v-model対応
   */
  @Prop({ type: String })
  value;

  @Prop({ type: String })
  placeholder;

  @Prop({ type: String })
  size;

  @Prop({ type: Boolean })
  disabled;

  /**
   * 親から渡された値を変換した内部データ（コード階層配列）を参照する
   *
   * v-model対応
   */
  get model () {
    return this.innerValue;
  }

  /**
   * 内部データ（コード階層配列）を親にコードとして通知する
   *
   * NOTE
   * 内部データとしてはコード階層配列として保持している、
   * 親コンポーネントに対してはコード階層の最下層のコードを通知する。
   * v-model対応
   */
  set model (hierarchy) {
    const value =
      Array.isArray(hierarchy) && hierarchy.length > 0
        ? hierarchy[hierarchy.length - 1]
        : null;
    this.innerValueCache[value] = hierarchy;
    this.$emit('input', value);
  }

  /**
   * 有効無効状態を参照する
   *
   * NOTE
   * 親コンポーネントからPropで指定された状態と、自身が管理する状態
   * の双方を参照して有効無効状態を算出しています。
   */
  get isDisabled () {
    return this.disabled || this.innerDisabled;
  }

  /**
   * 選択オプションデータのvalue値の項目名
   */
  get valueKey () {
    const props = this.props || {};
    return props.value || 'value';
  }

  /**
   * 選択オプションデータのchildren値の項目名
   */
  get childrenKey () {
    const props = this.props || {};
    return props.children || 'children';
  }

  /**
   * 選択オプションデータ項目のマッピング
   *
   * NOTE
   * 継承クラス側でオーバーライド対象
   */
  get props () {
    return {
      label: 'label',
      value: 'value',
      children: 'children',
      disabled: 'disabled'
    };
  }

  /**
   * 末端階層以外での選択可否
   *
   * NOTE
   * 継承クラス側でオーバーライド対象
   */
  get changeOnSelect () {
    return false;
  }

  /**
   * フィルター入力可否
   *
   * NOTE
   * 継承クラス側でオーバーライド対象
   */
  get filterable () {
    return false;
  }

  /**
   * 選択値のクリア可否
   *
   * NOTE
   * 継承クラス側でオーバーライド対象
   */
  get clearable () {
    return true;
  }

  /**
   * 値のセパレータ文字列
   *
   * NOTE
   * 継承クラス側でオーバーライド対象
   */
  get separator () {
    return '/';
  }

  /**
   * フィルター入力時の処理遅延時間
   *
   * NOTE
   * 継承クラス側でオーバーライド対象
   */
  get debounce () {
    return 300;
  }

  /**
   * 選択オプションを展開するトリガー
   *
   * NOTE
   * 継承クラス側でオーバーライド対象
   */
  get expandTrigger () {
    return 'click';
  }

  /**
   * 全選択オプション階層の表示有無
   *
   * NOTE
   * 継承クラス側でオーバーライド対象
   */
  get showAllLevels () {
    return true;
  }

  /**
   * 選択オプション要素のクラス指定
   *
   * NOTE
   * 継承クラス側でオーバーライド対象
   */
  get popperClass () {
    return null;
  }

  /**
   * フィルター処理の実行可否判定関数
   *
   * NOTE
   * 継承クラス側でオーバーライド対象
   */
  get beforeFilter () {
    return function () {
      return true;
    };
  }

  /**
   * 選択オプションの動的ローディング有無
   *
   * NOTE
   * 動的ローディングを利用するコンポーネントの場合は、継承クラス側でtrue
   * を返却するようにオーバーライドしてください。
   */
  get isDynamicLoadingCascader () {
    return false;
  }

  @sas.BindLoading({ activeOn: ['fetch*'] })
  @sas.BindCache({ transparent: true })
  @sas.BindApi
  @sas.BindDefault
  data () {
    return {
      selectOptions: [],
      innerValue: [],
      innerValueCache: { null: [] },
      innerDisabled: false,
      loading: false
    };
  }

  // 初期化
  initialize () {
    this.fetchOptions([]);
  }

  // 親コンポーネントへの透過イベント
  onBlur (...args) {
    this.$emit('blur', ...args);
  }

  // 親コンポーネントへの透過イベント
  onFocus (...args) {
    this.$emit('focus', ...args);
  }

  // 親コンポーネントへの透過イベント
  onChange (...args) {
    this.$emit('change', ...args);
  }

  // 親コンポーネントへの透過イベント
  onVisibleChange (...args) {
    this.$emit('visible-change', ...args);
  }

  /**
   * 親コンポーネントから渡されたデータを内部データ（コード階層配列）に変換する
   *
   * NOTE
   * 親コンポーネント側のデータ変更および、自コンポーネントでの選択オプション
   * 読込完了のタイミングで実行する。
   */
  @Watch('value')
  @Watch('selectOptions')
  fetchInnerValue () {
    const value = this.value || null;
    return this.fetchHierarchy(value)
      .then(hierarchy => (this.innerValue = hierarchy))
      .then(hierarchy => this.complementOptionHierarchy(hierarchy))
      .catch(reason => {
        if (!reason.isCanceled) {
          this.innerDisabled = true;
          this.innerValue = this.$_sas.$default.innerValue;
        }
        this.ignore(reason);
      });
  }

  /**
   * 指定したコード階層までの選択オプション読込を実行する
   */
  complementOptionHierarchy (hierarchy) {
    // 未取得の選択オプションの読込処理をPromise化する
    //
    // 動的ローディングが無効の場合は、空のPromiseを返却する。
    // 動的ローディングが有効の場合は、各コード階層の選択オプションを取得する
    // Promiseを返却する。
    //
    // 引数のコード階層が[1,2]の場合、[][1]の各コード階層に対する選択オプション
    // 読込処理を内包するPromiseを返却する。
    //
    // NOTE
    // 先頭の[]は初期化処理での読込対象であり、重複処理となるが、これに対する読
    // 込処理を省略すると、初期化処理での読込処理がキャンセルされ、処理に不整合
    // が生じる場合があるので省略してはならない。
    let promise = Promise.resolve();
    if (this.isDynamicLoadingCascader) {
      for (let i = 0; i <= hierarchy.length; i++) {
        const workHierarchy = hierarchy.slice(0, i);
        if (!this.isStored(workHierarchy)) {
          promise = promise.then(() => this.fetchOptions(workHierarchy));
        }
      }
    }
    return promise;
  }

  /**
   * 選択オプションを取得する
   */
  @sas.Asynchronous
  fetchOptions (hierarchy) {
    if (this.innerDisabled) {
      return Promise.resolve();
    }
    return this.fetchOptionsImpl(hierarchy)
      .then(options => this.storeOptions(hierarchy, options))
      .catch(reason => {
        if (!reason.isCanceled) {
          this.innerDisabled = true;
          this.selectOptions = this.$_sas.$default.selectOptions;
        }
        this.ignore(reason);
      });
  }

  /**
   * 指定したコード階層の選択オプションを読込済状態にする
   */
  addStored (hierarchy) {
    this.$_stored = this.$_stored ? this.$_stored : {};
    this.$_stored[JSON.stringify(hierarchy)] = true;
  }

  /**
   * 指定したコード階層の選択オプションが読込済か判定する
   */
  isStored (hierarchy) {
    this.$_stored = this.$_stored ? this.$_stored : {};
    return this.$_stored[JSON.stringify(hierarchy)] || false;
  }

  /**
   * 選択オプションを指定したコード階層に格納する
   */
  storeOptions (hierarchy, options) {
    // 既に格納済みであればスキップする
    if (this.isStored(hierarchy)) {
      return;
    }

    this.addStored(hierarchy);

    if (hierarchy.length === 0) {
      // ルートの選択オプションとして格納する
      this.selectOptions = options;
    } else {
      // hierarchyで指定された階層のchildrenとしてオプションを格納する
      // 空の配列の場合にはchildren要素自体を追加しないよう制御する
      // 画面上に子要素が存在することを示す記号">"が表示される為）
      if (options && options.length > 0) {
        const props = this.props || {};
        const childrenKey = props.children || 'children';
        const parentOption = this.findOption(hierarchy);
        if (parentOption) {
          parentOption[childrenKey] = options;
        }
      }
    }
  }

  /**
   * 指定したコード階層に対応する選択オプションを取得する
   */
  findOption (hierarchy) {
    let result = null;
    const valueKey = this.valueKey;
    const childrenKey = this.childrenKey;
    function recursiveSearch (hierarchy, options) {
      const needle = hierarchy.shift();
      options.some(option => {
        if (needle === option[valueKey]) {
          if (hierarchy.length === 0) {
            // 検索終了
            result = option;
            return true;
          } else {
            // 下層の検索処理
            return recursiveSearch(hierarchy, option[childrenKey]);
          }
        }
      });
      return false;
    }
    recursiveSearch(hierarchy.slice(0), this.selectOptions);
    return result;
  }

  /**
   * 選択オプションをサーバから取得する
   *
   * NOTE
   * この処理は継承クラス側で個別に実装する。
   * 成功時にはoptionsを返却するPromiseを返却するメソッドとして実装する。
   */
  fetchOptionsImpl (hierarchy) {
    throw new Error('SasCascader.fetchOptionsImpl must be overridden.');
  }

  /**
   * 指定されたコードに対応するコード階層配列を取得する
   */
  @sas.Asynchronous
  fetchHierarchy (value) {
    // 自身がemitで親に通知した値はキャッシュに格納されているので、まず自身のキャッシュを確認する。
    // それ以外の場合は選択オプションから算出、もしくはサーバから取得する。
    if (value in this.innerValueCache) {
      return Promise.resolve(this.innerValueCache[value]);
    } else {
      return this.fetchHierarchyImpl(value);
    }
  }

  /**
   * コード階層配列を取得する
   *
   * NOTE
   * デフォルトでは、内部で保持している選択オプションから算出する。
   * データ量が大量となる場合、および、選択オプションを動的ローディングする
   * 場合は、オーバーライドしてサーバに問い合わせするよう実装してください。
   *
   * 成功時にはhierarchy(コード階層の配列)を返却するPromiseを返却するメソッドとして実装する。
   */
  fetchHierarchyImpl (value) {
    return this.computeHierarchy(value);
  }

  /**
   * 自身で保持している選択オプションからコード階層を算出する
   *
   * fetchHierarcyImplのデフォルト実装
   */
  computeHierarchy (value) {
    // 再帰的なコード階層を構築処理
    const valueKey = this.valueKey;
    const childrenKey = this.childrenKey;
    function recursiveSearch (result, options, needle) {
      return options.some(option => {
        if (needle === option[valueKey]) {
          result.unshift(option[valueKey]);
          return true;
        } else {
          // 下層が合致した場合は自身のコードを階層に追加する
          if (
            option[childrenKey] &&
            recursiveSearch(result, option[childrenKey], needle)
          ) {
            result.unshift(option[valueKey]);
            return true;
          }
        }
      });
    }
    return new Promise(resolve => {
      const result = [];
      recursiveSearch(result, this.selectOptions || [], value);
      resolve(result);
    });
  }

  /**
   * 選択アイテムの変更イベントハンドラ
   */
  onActiveItemChange (hierarchy) {
    if (this.isDynamicLoadingCascader) {
      this.fetchOptions(hierarchy);
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/core/ui/SasCascader.vue